import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL, ApiError, login as apiLogin } from "../lib/api";
import { setCredentials } from "../lib/auth";
import { Alert, Spinner } from "../components/States";
import { Logo } from "../components/Logo";

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from ?? "/tenants";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      const res = await apiLogin(email.trim(), password);
      // The session token becomes the bearer credential; email drives the audit actor.
      setCredentials({ token: res.token, email: res.admin.email });
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Invalid email or password.");
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Could not reach the admin API. Verify the API URL and your connection.");
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={onSubmit}>
        <div className="logo">
          <Logo size={26} /> ActionConnect
        </div>
        <p className="lead">Platform admin console — staff only. Sign in to continue.</p>

        {error ? <Alert kind="error">{error}</Alert> : null}

        <label className="field">
          <span className="label">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus
            placeholder="you@actionconnect.com"
            required
          />
        </label>

        <label className="field">
          <span className="label">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            placeholder="••••••••"
            required
          />
        </label>

        <button className="btn primary" type="submit" disabled={pending} style={{ width: "100%" }}>
          {pending ? <Spinner label="Signing in…" /> : "Sign in"}
        </button>

        <p className="hint" style={{ marginTop: 16 }}>
          API: <code>{API_BASE_URL}</code>
        </p>
      </form>
    </div>
  );
}
